// src/services/authService.js
import { supabase } from './supabase';

export const ADMIN_EMAIL = 'reservas@clicktravelagency.com.br';
const WHATSAPP = '5541920004775';
const APP_NAME = 'Click Travel Agency';

// ── WEB PUSH ─────────────────────────────────────────────────
const SEND_PUSH_URL = '/.netlify/functions/send-push';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

export const isPushSupported = () => 'serviceWorker' in navigator && 'PushManager' in window;

// Pide permiso de notificaciones y, si se concede, suscribe al navegador
// vía Web Push y guarda el resultado en push_subscriptions. is_admin marca
// la fila para que las notificaciones internas (punto 3) puedan encontrarla.
export const subscribeToPush = async (uid, isAdmin) => {
  if (!isPushSupported()) return { ok: false, error: 'Este navegador no soporta notificaciones push.' };
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return { ok: false, error: `Permiso del navegador: "${permission}" (no concedido).` };

    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(process.env.REACT_APP_VAPID_PUBLIC_KEY),
      });
    }

    const json = subscription.toJSON();
    const { error } = await supabase.from('push_subscriptions').upsert({
      user_id: uid,
      endpoint: json.endpoint,
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
      is_admin: !!isAdmin,
    }, { onConflict: 'user_id,endpoint' });
    if (error) throw error;
    return { ok: true };
  } catch (err) {
    console.error('No se pudo activar las notificaciones:', err);
    return { ok: false, error: err.message || String(err) };
  }
};

async function sendPush(payload) {
  try {
    await fetch(SEND_PUSH_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    console.error('No se pudo enviar la notificación push:', err);
  }
}

// Todas las llamadas de abajo son "fire and forget": nunca deben bloquear
// ni retrasar el flujo de la UI (WhatsApp, guardado de perfil, etc.).
export const notifyUser  = (uid, body, url = '/')  => sendPush({ userIds: [uid], title: APP_NAME, body, url });
export const notifyUsers = (uids, body, url = '/') => sendPush({ userIds: uids, title: APP_NAME, body, url });
export const notifyAdmin = (body, url = '/')       => sendPush({ userIds: 'admin', title: APP_NAME, body, url });

export const notifyAdminConsulta = (serviceLabel, userName) =>
  notifyAdmin(`📩 Nueva consulta de ${serviceLabel} desde la app — ${userName}`, '/?tab=more&admin=solicitudes');

// Anuncio a TODOS los que tienen la app instalada y notificaciones
// activadas (promociones, avisos). A diferencia de notifyAdmin/notifyUser
// (fire-and-forget), esto sí espera la respuesta y la devuelve -- el admin
// necesita saber a cuántos les llegó antes de confiar en que salió. El
// server (send-push.js) verifica con este mismo token de sesión que quien
// llama es el admin real antes de difundir.
export const broadcastNotificacion = async (title, body) => {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(SEND_PUSH_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token || ''}` },
    body: JSON.stringify({ userIds: 'all', title, body }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'No se pudo enviar la difusión.');
  return data; // { sent, failed, deleted, total }
};

// Respaldo invisible para el cliente: se guarda en el mismo momento en que
// se abre WhatsApp, sin cambiar nada del flujo ni agregar pantallas nuevas.
// Es solo para que el dueño nunca pierda el rastro de un pedido si se le
// pierde el celular o el chat -- no reemplaza WhatsApp como confirmación.
export const registrarSolicitud = async ({ uid, tipo, detalle }) => {
  const { error } = await supabase.from('solicitudes').insert({
    user_id: uid,
    tipo,
    detalle,
  });
  if (error) throw error;
};

// Panel admin -- pestaña Solicitudes. Mismo patrón de "join en JS" que
// getVuelosReferidos: solicitudes no guarda datos del cliente, solo
// user_id, así que se resuelven aparte contra profiles.
export const getAllSolicitudes = async () => {
  const { data, error } = await supabase
    .from('solicitudes')
    .select('*')
    .order('created_at', { ascending: false });
  if (error || !data) return [];

  const ids = [...new Set(data.map(s => s.user_id))];
  const { data: profilesData } = await supabase
    .from('profiles')
    .select('id, name, email, phone, cpf')
    .in('id', ids);
  const profileMap = {};
  (profilesData || []).forEach(p => { profileMap[p.id] = p; });

  return data.map(s => ({ ...s, cliente: profileMap[s.user_id] || null }));
};

// ── AUTH ─────────────────────────────────────────────────────
// redirectTo = la URL completa actual (no solo el origen) -- así alguien
// que entra a loguearse desde /?tab=nuestros_clientes (ej. para dejar una
// reseña) vuelve exactamente ahí después de loguearse con Google, en vez
// de aterrizar en la pantalla de Inicio sin ningún rastro de a qué había
// venido. Confirmado como fricción real: sin esto, alguien sin la app ya
// instalada no tenía forma de saber que tenía que ir a buscar la sección
// de reseñas por su cuenta después del login.
export const loginWithGoogle = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: window.location.href },
  });
  if (error) throw error;
  return data;
};

export const sendEmailLink = async (email) => {
  const { error } = await supabase.auth.signInWithOtp({
    email: email.trim().toLowerCase(),
    options: { emailRedirectTo: window.location.origin },
  });
  if (error) throw error;
};

export const completeEmailSignIn = async () => null;

export const logoutUser = () => supabase.auth.signOut();

// ── PERFIL ───────────────────────────────────────────────────
export const findUserByUid = async (uid) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', uid)
    .single();
  if (error) return null;
  return { uid: data.id, ...data };
};

export const validatePhone = (phone) => {
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 8 && digits.length <= 15;
};

// Brasil: "55" + 2 dígitos de DDD + 9 dígitos del número = 13 dígitos exactos.
export const validatePhoneBrasil = (phone) => phone.replace(/\D/g, '').length === 13;

// Cuba: "53" + 8 dígitos = 10 dígitos exactos.
export const validatePhoneCuba = (phone) => {
  const digits = phone.replace(/\D/g, '');
  return digits.startsWith('53') && digits.length === 10;
};

export const checkDuplicates = async (uid, { cpf, phone }) => {
  const cpfClean   = cpf.replace(/\D/g, '');
  const phoneClean = phone.replace(/\D/g, '');

  const { data: cpfTaken } = await supabase.rpc('profile_cpf_taken', { p_cpf: cpfClean });
  if (cpfTaken) {
    return { field: 'cpf', message: 'Este CPF ya está registrado en otra cuenta.' };
  }

  const { data: phoneTaken } = await supabase.rpc('profile_phone_taken', { p_phone: phoneClean });
  if (phoneTaken) {
    return { field: 'phone', message: 'Este número de teléfono ya está registrado en otra cuenta.' };
  }

  return null;
};

export const saveUserProfile = async (uid, { cpf, phone, name, ubicacion }) => {
  const updates = {
    phone: phone.replace(/\D/g, ''),
    profile_complete: true,
    ubicacion,
  };
  if (cpf) updates.cpf = cpf.replace(/\D/g, '');
  if (name) updates.name = name.trim();

  const { error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', uid);
  if (error) throw error;
};

// Solo devuelve el uid dueño del código -- no el perfil completo, ya que
// esto se resuelve para CUALQUIER usuario (no solo el admin) al aplicar un
// referido, vía RPC SECURITY DEFINER (profile_id_by_referral_code).
export const findUserByReferralCode = async (code) => {
  const { data, error } = await supabase.rpc('profile_id_by_referral_code', {
    p_code: code.toUpperCase(),
  });
  if (error || !data) return null;
  return { uid: data };
};

// Aplica el código de referido pendiente (guardado en localStorage desde el
// parámetro ?ref= de la URL) al perfil del usuario que acaba de iniciar
// sesión. Se llama en cada carga de sesión — el .is('referred_by', null) en
// el WHERE asegura que nunca se sobrescriba un referred_by ya asignado,
// incluso si esta función corre más de una vez para el mismo usuario.
export const applyPendingReferralCode = async (uid) => {
  const pendingCode = localStorage.getItem('pending_referral_code');
  if (!pendingCode) return null;
  localStorage.removeItem('pending_referral_code');

  try {
    const referrer = await findUserByReferralCode(pendingCode);
    if (!referrer || referrer.uid === uid) return null;

    const { error } = await supabase
      .from('profiles')
      .update({ referred_by: referrer.uid })
      .eq('id', uid)
      .is('referred_by', null);
    if (error) throw error;

    return referrer.uid;
  } catch (err) {
    console.error('No se pudo aplicar el código de referido:', err);
    return null;
  }
};

export const findUserByCpf = async (cpf) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('cpf', cpf.replace(/\D/g, ''))
    .maybeSingle();
  if (error || !data) return null;
  return { uid: data.id, ...data };
};

export const findUserByEmail = async (email) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('email', email.trim().toLowerCase())
    .maybeSingle();
  if (error || !data) return null;
  return { uid: data.id, ...data };
};

export const findUserByName = async (name) => {
  const { data, error } = await supabase
    .from('profiles').select('*').ilike('name', `%${name.trim()}%`).limit(1);
  if (error || !data || data.length === 0) return null;
  return { uid: data[0].id, ...data[0] };
};

export const findReferredUsers = async (uid) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('name, email')
    .eq('referred_by', uid);
  if (error || !data) return [];
  return data;
};

// ── CLICKS ───────────────────────────────────────────────────
export const addClicks = async (uid, amount) => {
  const { error } = await supabase.rpc('increment_clicks', {
    user_id: uid,
    amount: parseInt(amount),
  });
  if (error) throw error;
};

export const resetClicks = async (uid) => {
  const { error } = await supabase.rpc('reset_clicks', { user_id: uid });
  if (error) throw error;
};

// Otorga el bono de bienvenida (50 CLICKs) de forma atómica e idempotente:
// el RPC solo suma los CLICKs y marca welcome_bonus_claimed=true si aún no
// se había reclamado. Devuelve true si esta llamada fue la que lo otorgó,
// false si el usuario ya lo tenía (reintento de guardado, edición, etc.).
export const claimWelcomeBonus = async (uid) => {
  const { data, error } = await supabase.rpc('claim_welcome_bonus', { user_id: uid });
  if (error) throw error;
  return !!data;
};

// Marca referral_bonus_claimed = true en el perfil de uid. Usa RPC (SECURITY DEFINER)
// porque el admin actualiza el perfil de OTRO usuario: un UPDATE directo desde el
// cliente queda sujeto a RLS ("solo puedes editar tu propio perfil") y falla en
// silencio (0 filas afectadas, sin error) en vez de lanzar una excepción.
export const claimReferralBonus = async (uid) => {
  const { error } = await supabase.rpc('claim_referral_bonus', { user_id: uid });
  if (error) throw error;
};

// Reinicia el ciclo de canjes: pone los CLICKs en 0 y marca el inicio de un nuevo ciclo.
export const resetClicksCycle = async (uid) => {
  const { error } = await supabase
    .from('profiles')
    .update({ clicks: 0, clicks_reset_at: new Date().toISOString() })
    .eq('id', uid);
  if (error) throw error;
};

// RPC (SECURITY DEFINER) porque el admin registra historial de OTROS
// usuarios (addServiceClicks, bono de referido) además del propio -- un
// insert directo queda sujeto a RLS y solo permite user_id = auth.uid().
export const logClicksHistory = async (uid, amount, reason) => {
  const { error } = await supabase.rpc('log_clicks_history', {
    p_user_id: uid,
    p_amount: amount,
    p_reason: reason,
  });
  if (error) throw error;
};

export const getClicksHistory = async (uid) => {
  const { data, error } = await supabase
    .from('clicks_history')
    .select('*')
    .eq('user_id', uid)
    .order('created_at', { ascending: false });
  if (error) return [];
  return data;
};

// Agrega CLICKs por un servicio, aplica automáticamente el bono de referido
// correspondiente y dispara las notificaciones push del punto 2: el total
// se rastrea antes/después de TODAS las sumas de esta llamada (servicio +
// bonos de referido propios) para no perderse un cruce de nivel que ocurra
// solo por el bono acumulado. Cualquier nivel cruzado -- por el servicio
// propio o por el bono que recibe el referente -- genera su oportunidad de
// canje (nunca se salta uno, sin importar cómo se llegó a él).
export const addServiceClicks = async (uid, amount) => {
  const user = await findUserByUid(uid);
  if (!user) throw new Error('Usuario no encontrado.');

  const previousTotal = user.clicks || 0;
  let runningTotal = previousTotal;

  await addClicks(uid, amount);
  runningTotal += amount;
  await logClicksHistory(uid, amount, 'servicio_manual');

  const referralNotifications = [];
  if (user.referred_by) {
    const referrer = await findUserByUid(user.referred_by);
    const referrerPrevious = referrer?.clicks || 0;
    let referrerRunning = referrerPrevious;

    if (!user.referral_bonus_claimed) {
      await addClicks(uid, 50);
      runningTotal += 50;
      await addClicks(user.referred_by, 50);
      referrerRunning += 50;
      await claimReferralBonus(uid);
      await logClicksHistory(uid, 50, 'referido_bono');
      await logClicksHistory(user.referred_by, 50, 'referido_bono');
      referralNotifications.push(() => {
        const texto = '🙌 ¡Tu referido completó su primer servicio! Ganaste 50 CLICKs de bonificación.';
        notifyUser(uid, texto, '/?tab=clicks');
        notifyUser(user.referred_by, texto, '/?tab=clicks');
      });
    } else {
      const bono = Math.ceil(amount * 0.20);
      await addClicks(user.referred_by, bono);
      referrerRunning += bono;
      await logClicksHistory(user.referred_by, bono, 'referido_recurrente');
      referralNotifications.push(() => {
        notifyUser(user.referred_by, `✨ Tu referido hizo un nuevo servicio. Ganaste ${bono} CLICKs.`, '/?tab=clicks');
      });
    }

    await registrarNivelesCruzados(user.referred_by, referrerPrevious, referrerRunning);
  }

  const crossed = await registrarNivelesCruzados(uid, previousTotal, runningTotal);
  if (!crossed.length) {
    notifyUser(uid, `🎉 ¡Recibiste ${amount} CLICKs! Ya tienes ${runningTotal} CLICKs acumulados.`, '/?tab=clicks');
  }

  referralNotifications.forEach(fn => fn());
};

// ── TASAS DE CAMBIO ──────────────────────────────────────────
export const getRates = async () => {
  const { data, error } = await supabase
    .from('app_config')
    .select('rate_low, rate_high, rate_brl_to_usd, rate_usd_to_brl')
    .eq('key', 'exchange')
    .single();
  if (error || !data) return { low: 0, high: 0, brlToUsd: 0, usdToBrl: 0 };
  return {
    low: data.rate_low ?? 0,
    high: data.rate_high ?? 0,
    brlToUsd: data.rate_brl_to_usd ?? 0,
    usdToBrl: data.rate_usd_to_brl ?? 0,
  };
};

export const getRateForAmount = (rates, brlAmount) =>
  parseFloat(brlAmount) >= 100 ? rates.high : rates.low;

// ── PLANES / SUSCRIPCIONES ───────────────────────────────────
export const getSubscriptionsEnabled = async () => {
  const { data, error } = await supabase
    .from('app_config')
    .select('subscriptions_enabled')
    .limit(1)
    .single();
  if (error || !data) return false;
  return data.subscriptions_enabled === true;
};

export const updateRates = async ({ low, high, brlToUsd, usdToBrl }) => {
  const updates = { updated_at: new Date().toISOString() };
  if (low      !== undefined) updates.rate_low        = parseFloat(low);
  if (high     !== undefined) updates.rate_high       = parseFloat(high);
  if (brlToUsd !== undefined) updates.rate_brl_to_usd = parseFloat(brlToUsd);
  if (usdToBrl !== undefined) updates.rate_usd_to_brl = parseFloat(usdToBrl);

  const { error } = await supabase
    .from('app_config')
    .update(updates)
    .eq('key', 'exchange');
  if (error) throw error;
};

// ── HORARIO COMERCIAL ────────────────────────────────────────
export const BUSINESS_HOURS = { open: 10, close: 22 };

export const isBusinessOpen = () => {
  const hour = new Date().getHours();
  return hour >= BUSINESS_HOURS.open && hour < BUSINESS_HOURS.close;
};

export const getBusinessHoursLabel = () =>
  `${BUSINESS_HOURS.open}:00 am – ${BUSINESS_HOURS.close - 12}:00 pm`;

// ── UTILIDADES ───────────────────────────────────────────────
export const openWhatsApp = (message) => {
  window.open(`https://api.whatsapp.com/send?phone=${WHATSAPP}&text=${encodeURIComponent(message)}`, '_blank');
};

export const getRewardLevel = (clicks) => {
  if (clicks >= 2000) return 2000;
  if (clicks >= 1500) return 1500;
  if (clicks >= 1000) return 1000;
  if (clicks >= 500)  return 500;
  return null;
};

// Tabla de recompensas por nivel (punto 4b) -- hardcodeada en el código, no
// en Supabase. recharge/discounted son la recarga con 50% de descuento
// (siempre); traduccion/vuelo solo existen a partir de 1000/1500 e incluyen
// los días de validez del vale generado al reclamarlos.
export const REWARD_LEVELS = {
  500:  { recharge: 20, discounted: 10 },
  1000: { recharge: 25, discounted: 13, traduccion: { descuento: 'R$15 de descuento en una traducción', dias: 30 } },
  1500: { recharge: 35, discounted: 17, traduccion: { descuento: 'R$25 de descuento en una traducción', dias: 30 }, vuelo: { descuento: '10 USD de descuento', dias: 90 } },
  2000: { recharge: 50, discounted: 25, traduccion: { descuento: 'R$35 de descuento en una traducción', dias: 30 }, vuelo: { descuento: '10 USD de descuento', dias: 90 }, resetOnClaim: true },
};

export const getRewardDetails = (level) => REWARD_LEVELS[level] || null;

// Arma la descripción del premio para el texto de la notificación de nivel
// (punto 2b): "Recarga de R$X por R$Y + vale de traducción con... + vale de vuelo con...".
export const getRewardDescription = (level) => {
  const r = REWARD_LEVELS[level];
  if (!r) return '';
  const parts = [`Recarga de R$${r.recharge} por R$${r.discounted}`];
  if (r.traduccion) parts.push(`vale de traducción con ${r.traduccion.descuento}`);
  if (r.vuelo) parts.push(`vale de vuelo con ${r.vuelo.descuento}`);
  return parts.join(' + ') + '.';
};

const LEVELS_ORDER = [500, 1000, 1500, 2000];

// Nivel más alto cruzado entre previousTotal y newTotal. Se mantiene por si
// algo más solo necesita saber cuál es, pero el registro de recompensas usa
// getCrossedLevels (abajo) para no saltarse ninguno.
export const getHighestCrossedLevel = (previousTotal, newTotal) => {
  let highest = null;
  for (const lvl of LEVELS_ORDER) {
    if (previousTotal < lvl && newTotal >= lvl) highest = lvl;
  }
  return highest;
};

// TODOS los niveles cruzados entre previousTotal y newTotal (previousTotal <
// nivel <= newTotal), en orden. Si un depósito o bono salta varios niveles
// de una vez (ej. 450 -> 1600), cada uno de ellos debe poder canjearse --
// llegar a un nivel, sin importar el camino, siempre abre su oportunidad de
// canje.
export const getCrossedLevels = (previousTotal, newTotal) =>
  LEVELS_ORDER.filter((lvl) => previousTotal < lvl && newTotal >= lvl);

export const getProgress = (clicks) => {
  if (clicks < 500)  return { pct: clicks / 500,          next: 500,  current: 0    };
  if (clicks < 1000) return { pct: (clicks - 500) / 500,  next: 1000, current: 500  };
  if (clicks < 1500) return { pct: (clicks - 1000) / 500, next: 1500, current: 1000 };
  if (clicks < 2000) return { pct: (clicks - 1500) / 500, next: 2000, current: 1500 };
  return { pct: 1, next: 2000, current: 2000 };
};

export const formatCpf = (cpf = '') => {
  const d = cpf.replace(/\D/g, '');
  if (d.length !== 11) return cpf;
  return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`;
};

// ── CONTACTOS ────────────────────────────────────────────────
export const getContacts = async (uid) => {
  const { data, error } = await supabase
    .from('contacts').select('*').eq('user_id', uid).order('created_at');
  if (error) return [];
  return data;
};

export const saveContact = async (uid, contact) => {
  if (contact.id) {
    const { error } = await supabase
      .from('contacts').update(contact).eq('id', contact.id);
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('contacts').insert({ ...contact, user_id: uid });
    if (error) throw error;
  }
};

export const deleteContact = async (id) => {
  const { error } = await supabase.from('contacts').delete().eq('id', id);
  if (error) throw error;
};

// ── CANJES ───────────────────────────────────────────────────
export const getUserCanjes = async (uid) => {
  const { data, error } = await supabase
    .from('canjes')
    .select('*')
    .eq('user_id', uid)
    .order('created_at', { ascending: false });
  if (error) return [];
  return data;
};

export const getAllCanjes = async () => {
  const { data, error } = await supabase
    .from('canjes')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) return [];
  return data;
};

export const createCanje = async ({ uid, userName, userEmail, level, rechargePrice, discountedPrice }) => {
  const { error } = await supabase.from('canjes').insert({
    user_id: uid,
    user_name: userName,
    user_email: userEmail,
    level,
    recharge_price: rechargePrice,
    discounted_price: discountedPrice,
    status: 'pendiente',
  });
  if (error) throw error;
};

export const markCanjeProcessed = async (id) => {
  const { error } = await supabase.from('canjes').update({ status: 'procesado' }).eq('id', id);
  if (error) throw error;
};

// ── VALES / NIVELES DE RECOMPENSA (punto 4) ─────────────────────
// Fila de nivel_recompensas del ciclo actual (created_at >= clicks_reset_at,
// mismo patrón que isRedeemedThisCycle en ClicksScreen) para un nivel dado.
export const getNivelRecompensas = async (uid) => {
  const { data, error } = await supabase
    .from('nivel_recompensas')
    .select('*')
    .eq('user_id', uid)
    .order('alcanzado_at', { ascending: false });
  if (error) return [];
  return data;
};

export const registrarNivelAlcanzado = async (uid, nivel) => {
  const { data, error } = await supabase.rpc('registrar_nivel_alcanzado', { p_user_id: uid, p_nivel: nivel });
  if (error) throw error;
  return data;
};

// Registra en nivel_recompensas CADA nivel cruzado entre previousTotal y
// newTotal (no solo el más alto) y notifica el más alto con una sola push.
// Se usa tanto para quien recibe el servicio como para el referente que
// cruza un nivel solo por el bono acumulado -- así cualquier camino hacia
// un nivel (propio o por bonos) siempre habilita su canje.
const registrarNivelesCruzados = async (uid, previousTotal, newTotal) => {
  const crossed = getCrossedLevels(previousTotal, newTotal);
  for (const nivel of crossed) {
    try {
      await registrarNivelAlcanzado(uid, nivel);
    } catch (err) {
      console.error('No se pudo registrar el nivel alcanzado:', err);
    }
  }
  if (crossed.length) {
    const highest = crossed[crossed.length - 1];
    notifyUser(uid, `🏆 ¡Llegaste a ${highest} CLICKs! ${getRewardDescription(highest)} Ve a CLICKs para canjearlo.`, '/?tab=clicks');
  }
  return crossed;
};

export const reclamarRecompensaRecarga = async (uid, nivel) => {
  const { error } = await supabase.rpc('reclamar_recompensa_recarga', { p_user_id: uid, p_nivel: nivel });
  if (error) throw error;
};

export const reclamarVale = async (uid, nivel, tipo, descuento) => {
  const { data, error } = await supabase.rpc('reclamar_vale', {
    p_user_id: uid, p_nivel: nivel, p_tipo: tipo, p_descuento: descuento,
  });
  if (error) throw error;
  return data;
};

export const getUserVales = async (uid) => {
  const { data, error } = await supabase
    .from('vales')
    .select('*')
    .eq('user_id', uid)
    .order('created_at', { ascending: false });
  if (error) return [];
  return data;
};

// Panel admin -- pestaña Canjes / sub-sección Vales (punto 4d).
export const buscarValePorCodigo = async (codigo) => {
  const { data, error } = await supabase.rpc('buscar_vale_por_codigo', { p_codigo: codigo });
  if (error) throw error;
  return data?.[0] || null;
};

export const marcarValeUsado = async (valeId) => {
  const { error } = await supabase.rpc('marcar_vale_usado', { p_vale_id: valeId });
  if (error) throw error;
};

// ── COTIZACIONES DE VUELO (sistema de referidos) ────────────────
// Registra cada cotización enviada desde el formulario de Vuelos, junto con
// quién refirió al usuario (si aplica). Se guarda incluso sin referred_by
// para tener histórico completo de Vuelos.
export const logVueloCotizacion = async ({ uid, referredBy, origen, destino, fechaSolicitada, adultos, infantes }) => {
  const { error } = await supabase.from('vuelos_cotizaciones').insert({
    user_id: uid,
    referred_by: referredBy || null,
    origen,
    destino,
    fecha_solicitada: fechaSolicitada,
    adultos,
    infantes,
  });
  if (error) throw error;
};

// Solo cotizaciones con referido, no pagadas primero y luego por fecha
// descendente dentro de cada grupo. Los nombres/correos de quien cotizó y
// de quien refirió se resuelven aparte (misma tabla profiles) porque
// vuelos_cotizaciones tiene dos FKs a profiles.
export const getVuelosReferidos = async () => {
  const { data, error } = await supabase
    .from('vuelos_cotizaciones')
    .select('*')
    .not('referred_by', 'is', null)
    .order('pagado', { ascending: true })
    .order('created_at', { ascending: false });
  if (error || !data) return [];

  const ids = [...new Set(data.flatMap(r => [r.user_id, r.referred_by]))];
  const { data: profilesData } = await supabase
    .from('profiles')
    .select('id, name, email')
    .in('id', ids);
  const profileMap = {};
  (profilesData || []).forEach(p => { profileMap[p.id] = p; });

  return data.map(r => ({
    ...r,
    cotizante: profileMap[r.user_id] || null,
    referidor: profileMap[r.referred_by] || null,
  }));
};

// Marca una cotización referida como pagada. Usa RPC (SECURITY DEFINER)
// porque el admin actualiza la fila de OTRO usuario — mismo patrón que
// claim_referral_bonus, nunca un .update() directo.
export const markVueloReferidoPagado = async (cotizacionId, personas) => {
  const { error } = await supabase.rpc('marcar_referido_vuelo_pagado', {
    cotizacion_id: cotizacionId,
    personas,
  });
  if (error) throw error;
};

export const validateCpf = (cpf) => {
  const d = cpf.replace(/\D/g, '');
  if (d.length !== 11 || new Set(d).size === 1) return false;
  let s = 0;
  for (let i = 0; i < 9; i++) s += parseInt(d[i]) * (10 - i);
  if ((s * 10 % 11) % 10 !== parseInt(d[9])) return false;
  s = 0;
  for (let i = 0; i < 10; i++) s += parseInt(d[i]) * (11 - i);
  return (s * 10 % 11) % 10 === parseInt(d[10]);
};
